import styled from '@emotion/styled';

import { breakpoints, screen } from '@/components/common/breakpoints';
import { IconOnlyButton, SecondaryButton } from '@/components/common/Buttons';
import { RichHeading } from '@/components/common/Heading';
import { HeadingLevelProvider } from '@/components/common/Heading/context';
import { Heading, Paragraph } from '@/components/common/MarkUp';
import { ProgrammeCard } from '@/components/common/ProgrammeCard';
import { Slider } from '@/components/common/SliderCommon/Slider';
import { Tag } from '@/components/common/Tag';
import { Section } from '@/components/layout/Section';
import { getButtonComponentTheme } from '@/lib/colourUtils';
import { useMatchMedia } from '@/lib/useMatchMedia';

import type { SanityProgrammeCarouselType } from '@/types/sanity';

export interface ProgrammeCarouselProps extends SanityProgrammeCarouselType {}

const sliderSettings = {
  infinite: false,
  speed: 500,
  slidesToScroll: 1,
  variableWidth: true,
  slidesToShow: 4,
  edgeFriction: 0,
  adaptiveHeight: true,
  responsive: [
    {
      breakpoint: 640,
      settings: {
        slidesToShow: 1,
      },
    },
    {
      breakpoint: 960,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 2200,
      settings: {
        slidesToShow: 4,
      },
    },
  ],
};

const ProgrammeCarousel = ({
  tag,
  mainHeading,
  caption,
  heading,
  backgroundColour,
  carouselItems,
  sliderTrackingTag,
  action,
  isCaptionUppercase = true,
}: ProgrammeCarouselProps) => {
  const backgroundColourToken = backgroundColour?.token;
  const carouselItemCount = carouselItems.length;
  const isMobile = useMatchMedia(breakpoints.sm);
  const displayAsSlider = carouselItemCount > 3 || isMobile;

  const bgColour = backgroundColourToken || '--surface-blue-tint';

  const carouselItemsMapped = carouselItems.map((carouselItem) => (
    <ProgrammeCard
      key={carouselItem.heading}
      caption={carouselItem.caption}
      heading={carouselItem.heading}
      body={carouselItem.body}
      icon={carouselItem.icon}
      bgColour={bgColour}
      url={carouselItem.url}
      ctaCaption={carouselItem.ctaCaption}
      featuresCaption={carouselItem.featuresCaption}
      features={carouselItem.features}
      trackingTag={carouselItem?.trackingTag}
    />
  ));
  return (
    <Section verticalPadding>
      <HeadingLevelProvider level={2}>
        {tag?.tagText && (
          <Tag
            bgColour={tag?.tagColour}
            margin="0 0 var(--spacing-xxx-small) 0"
          >
            {tag.tagText}
          </Tag>
        )}
        {caption && (
          <Caption isCaptionUppercase={isCaptionUppercase} size="caption-lg">
            {caption}
          </Caption>
        )}
        <HeadingContainer>
          {!!mainHeading && (
            <RichHeading size="headline-lg" heading={mainHeading} />
          )}
        </HeadingContainer>
        {heading && <SubHeading size="body-md-semi-bold">{heading}</SubHeading>}

        {displayAsSlider ? (
          <StyledSlider
            settings={sliderSettings}
            heading="slider heading field"
            nextArrow={
              <IconOnlyButton
                componentTheme={getButtonComponentTheme(backgroundColourToken)}
                aria-label="next slides"
                className={
                  sliderTrackingTag?.blockName &&
                  `tracking-${sliderTrackingTag.blockName}__${sliderTrackingTag.blockAction}-next`
                }
              />
            }
            prevArrow={
              <IconOnlyButton
                componentTheme={getButtonComponentTheme(backgroundColourToken)}
                icon="ArrowLeft"
                aria-label="previous slides"
                className={
                  sliderTrackingTag?.blockName &&
                  `tracking-${sliderTrackingTag.blockName}__${sliderTrackingTag.blockAction}-previous`
                }
              />
            }
            slideItems={carouselItems}
            slideItemTitleKey="heading"
          >
            {carouselItemsMapped}
          </StyledSlider>
        ) : (
          <CardContainerNonSlider itemCount={carouselItemCount}>
            {carouselItemsMapped}
          </CardContainerNonSlider>
        )}
        {action.buttonLabel && (
          <StyledButton
            componentTheme="light"
            key={action.buttonLabel}
            href={action.buttonLink}
            aria-label={action.buttonAccessibleLabel}
            className={
              action.linkTrackingTag?.blockName &&
              `tracking-${action.linkTrackingTag.blockName}__${action.linkTrackingTag.blockAction}`
            }
          >
            {action.buttonLabel}
          </StyledButton>
        )}
      </HeadingLevelProvider>
    </Section>
  );
};

const HeadingContainer = styled.div`
  margin-bottom: var(--spacing-400);
`;

const Caption = styled(Paragraph)<{ isCaptionUppercase?: boolean }>`
  color: var(--text-action);
  margin-bottom: var(--spacing-xx-small);
  margin-top: var(--spacing-400);
  text-transform: ${({ isCaptionUppercase }) =>
    isCaptionUppercase ? 'uppercase' : 'none'};
  ${screen.sm} {
    margin-top: var(--spacing-500);
  }
`;

const SubHeading = styled(Heading)`
  border-top: solid var(--border-warm-subtle) 1px;
  margin-bottom: var(--spacing-small);
`;

const StyledButton = styled(SecondaryButton)`
  position: absolute;
  bottom: 0px;
`;

const StyledSlider = styled(Slider)`
  display: flex;
  flex-direction: row;
  padding-bottom: var(--spacing-800);
  margin-bottom: var(--spacing-500);

  .slick-track {
    display: flex;
  }

  .slick-list {
    overflow: visible;
    width: 100%;
  }

  .slick-slide {
    max-width: 290px;
    min-width: 220px;
    width: 100%;
    padding-left: var(--spacing-small);
    &:first-of-type {
      padding-left: 0;
    }
    height: auto;

    > div {
      height: 100%;

      > div {
        height: 100%;
      }
    }

    ${screen.sm} {
      max-width: calc(((100vw - (var(--section-gutter) * 2)) / 4));
    }
  }

  .slick-disabled {
    opacity: 50%;
  }

  .slick-prev {
    left: auto;
    bottom: -76px;
    right: var(--spacing-500);
  }

  .slick-next {
    bottom: -76px;
    left: auto;
    right: 0;
  }

  .slick-prev,
  .slick-next {
    position: absolute;
    display: block;
    cursor: pointer;
    bottom: calc(var(--spacing-150) * -1);
    top: unset;
    padding: 0;
    border-radius: 100%;
    outline-offset: 2px;

    background-color: #2424561f;
    border-radius: 50%;
  }

  .slick-prev:hover,
  .slick-prev:focus,
  .slick-next:hover,
  .slick-next:focus {
    background-color: #2424561f;
  }

  ${screen.sm} {
    .slick-prev {
      right: var(--spacing-800);
    }
  }
  ${screen.md} {
    padding-bottom: var(--spacing-1000);
  }
`;
const CardContainerNonSlider = styled.div<{ itemCount: number }>`
  display: grid;
  ${({ itemCount }) => `grid-template-columns: repeat(${itemCount}, 1fr)`};
  gap: var(--spacing-small);
  width: 100%;

  > div,
  > a > div {
    width: 100%;
    max-width: unset;
  }
  margin-bottom: var(--spacing-400);
  padding-bottom: var(--spacing-1000);
`;

export { ProgrammeCarousel };
